import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { Children, cloneElement } from 'react';

import { customStylesWarning } from '../../console-messages';
import type { ResponsiveType } from '../../types';
import {
  getResponsiveNumber,
  getResponsiveOneOf,
  getSize,
} from '../../utilities';

import { getListContainer } from '../../utilities/styles';

const getSizeStyles = props => css`
  ${getSize('width', props.width)};
  ${getSize('height', props.height)};
  ${getSize('min-width', props.minWidth)};
  ${getSize('min-height', props.minHeight)};
  ${getSize('max-width', props.maxWidth)};
  ${getSize('max-height', props.maxHeight)};
`;

const SplitWrapper = styled.div<SplitWrapperProps>`
  display: flex;
  ${props => getResponsiveOneOf('align-items', props.alignItems)};
  ${props =>
    props.justifyContent &&
    getResponsiveOneOf('justify-content', props.justifyContent)};
  ${props =>
    props.alignContent &&
    getResponsiveOneOf('align-content', props.alignContent)};
  ${props => getResponsiveOneOf('flex-wrap', props.wrap)};
  ${props =>
    props.direction && getResponsiveOneOf('flex-direction', props.direction)};
  ${props => props.gap && getSize('gap', props.gap)};
  ${props => props.rowGap && getSize('row-gap', props.rowGap)};
  ${props => props.columnGap && getSize('column-gap', props.columnGap)};
  ${props => getSizeStyles(props)};
  ${props => getListContainer(props.as)};
`;

const SplitSectionWrapper = styled.div<SplitSectionWrapperProps>`
  ${props => getResponsiveNumber('flex-grow', props.grow)};
  ${props => getResponsiveNumber('flex-shrink', props.shrink)};
  ${props => props.basis && getSize('flex-basis', props.basis)};
  ${props => getSizeStyles(props)};
`;

export const SplitSection = ({
  children,
  grow,
  shrink,
  basis,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  htmlTag = 'div',
  className,
  style,
  ...props
}: SplitSectionProps & React.ComponentProps<typeof SplitSectionWrapper>) => {
  if (className || style) {
    // eslint-disable-next-line no-console
    console.warn(customStylesWarning);
  }

  return (
    <SplitSectionWrapper
      as={htmlTag}
      grow={grow}
      shrink={shrink}
      basis={basis}
      width={width}
      height={height}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      {...props}
    >
      {children}
    </SplitSectionWrapper>
  );
};

const Split = ({
  children,
  gap = 'initial',
  rowGap,
  columnGap,
  alignItems = 'center',
  justifyContent,
  alignContent,
  wrap = 'wrap',
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  htmlTag = 'div',
  className,
  style,
  ...props
}: SplitProps & React.ComponentProps<typeof SplitWrapper>) => {
  if (className || style) {
    // eslint-disable-next-line no-console
    console.warn(customStylesWarning);
  }

  return (
    <SplitWrapper
      as={htmlTag}
      gap={gap}
      rowGap={rowGap}
      columnGap={columnGap}
      alignItems={alignItems}
      justifyContent={justifyContent}
      alignContent={alignContent}
      wrap={wrap}
      width={width}
      height={height}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      {...props}
    >
      {htmlTag === 'ul' || htmlTag === 'ol'
        ? Children.map(children, (child: React.ReactElement) => {
            if (child.type === SplitSection || child.type === Split) {
              return cloneElement(child, { htmlTag: 'li' });
            }

            return child;
          })
        : children}
    </SplitWrapper>
  );
};

interface SharedProps {
  width?: ResponsiveType<number | string>;
  height?: ResponsiveType<number | string>;
  minWidth?: ResponsiveType<number | string>;
  minHeight?: ResponsiveType<number | string>;
  maxWidth?: ResponsiveType<number | string>;
  maxHeight?: ResponsiveType<number | string>;
  children?: React.ReactNode;
}

interface SplitProps extends SharedProps {
  alignItems?: ResponsiveType<
    'start' | 'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline'
  >;
  justifyContent?: ResponsiveType<
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
  >;
  alignContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'stretch'
    | 'start'
    | 'end'
    | 'baseline';
  wrap?: ResponsiveType<'nowrap' | 'wrap' | 'wrap-reverse'>;
  htmlTag?: 'div' | 'ul' | 'ol' | 'li';
  gap?: ResponsiveType<number | string>;
  rowGap?: ResponsiveType<number | string>;
  columnGap?: ResponsiveType<number | string>;
  direction?: ResponsiveType<
    'column' | 'column-reverse' | 'row' | 'row-reverse'
  >;
  className?: never;
  style?: never;
}

interface SplitWrapperProps extends SplitProps {
  as?: string;
}

interface SplitSectionProps extends SharedProps {
  grow?: ResponsiveType<number | string>;
  shrink?: ResponsiveType<number | string>;
  basis?: ResponsiveType<number | string>;
  className?: never;
  style?: never;
  htmlTag?: 'div' | 'li';
}

interface SplitSectionWrapperProps extends SplitSectionProps {
  as?: string;
}

export default Split;
